// Attendance / payroll **cycle** helpers — the frontend mirror of the backend
// `candidates/cycle_utils.py`. A cycle runs from the 26th of month M (inclusive)
// to the 25th of month M+1 (inclusive); the 26th of the end month belongs to the
// next cycle. A cycle is identified by its START month/year and the canonical
// query param is "YYYY-MM" of that start month.
//
// Payroll mapping (PAYROLL_MONTH_OFFSET = 1): the cycle starting 26-Apr-2026 is
// paid as "May 2026".

export const CYCLE_START_DAY = 26;
export const PAYROLL_MONTH_OFFSET = 1;

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const pad2 = (n) => String(n).padStart(2, '0');

// Shift a 1-based month by `delta` months. Returns { month, year }.
const shiftMonth = (month, year, delta) => {
  const idx = (month - 1) + delta;
  const newYear = year + Math.floor(idx / 12);
  const newMonth = ((idx % 12) + 12) % 12 + 1;
  return { month: newMonth, year: newYear };
};

const iso = (year, month, day) => `${year}-${pad2(month)}-${pad2(day)}`;

/**
 * Build a full cycle descriptor from its START month/year.
 * @returns {{cycle,startISO,endISO,startMonth,startYear,endMonth,endYear,label,payrollMonthName,payrollYear}}
 */
export const getCycleByStart = (startMonth, startYear) => {
  const { month: endMonth, year: endYear } = shiftMonth(startMonth, startYear, 1);
  // end inclusive = 25th of the next month (the day before the next cycle start)
  const endInclusive = new Date(endYear, endMonth - 1, CYCLE_START_DAY - 1);
  const { month: pMonth, year: payrollYear } = shiftMonth(startMonth, startYear, PAYROLL_MONTH_OFFSET);
  return {
    cycle: `${startYear}-${pad2(startMonth)}`,
    startISO: iso(startYear, startMonth, CYCLE_START_DAY),
    endISO: `${endInclusive.getFullYear()}-${pad2(endInclusive.getMonth() + 1)}-${pad2(endInclusive.getDate())}`,
    startMonth, startYear,
    endMonth, endYear,
    label: `${CYCLE_START_DAY} ${MONTH_ABBR[startMonth - 1]} – ${endInclusive.getDate()} ${MONTH_ABBR[endInclusive.getMonth()]}`,
    payrollMonthName: MONTH_NAMES[pMonth - 1],
    payrollYear,
  };
};

/** Cycle containing a given Date: on/after the 25th it starts this month, else previous. */
export const getCycleForDate = (date = new Date()) => {
  if (date.getDate() >= CYCLE_START_DAY) {
    return getCycleByStart(date.getMonth() + 1, date.getFullYear());
  }
  const { month, year } = shiftMonth(date.getMonth() + 1, date.getFullYear(), -1);
  return getCycleByStart(month, year);
};

export const getCurrentCycle = () => getCycleForDate(new Date());

/** Parse a "YYYY-MM" cycle param into a cycle descriptor. */
export const getCycleByParam = (param) => {
  const [y, m] = String(param).split('-').map((x) => parseInt(x, 10));
  return getCycleByStart(m, y);
};

/** The cycle that feeds a given payroll month. "May" 2026 → 25-Apr→25-May cycle. */
export const getCycleForPayrollMonth = (payrollMonthNumber, payrollYear) => {
  const { month, year } = shiftMonth(payrollMonthNumber, payrollYear, -PAYROLL_MONTH_OFFSET);
  return getCycleByStart(month, year);
};

/** List the most recent `count` cycles (newest first), starting from the current one. */
export const listCycles = (count = 12, from = new Date()) => {
  const current = getCycleForDate(from);
  const out = [];
  for (let i = 0; i < count; i++) {
    const { month, year } = shiftMonth(current.startMonth, current.startYear, -i);
    out.push(getCycleByStart(month, year));
  }
  return out;
};

/** Canonical query param ("YYYY-MM") for a cycle descriptor or its parts. */
export const cycleParam = (cycle) => (typeof cycle === 'string' ? cycle : cycle?.cycle);

/** Months a cycle spans, as [{year, month}] (start month then end month). */
export const monthsInCycle = (cycle) => {
  const c = typeof cycle === 'string' ? getCycleByParam(cycle) : cycle;
  return [
    { year: c.startYear, month: c.startMonth },
    { year: c.endYear, month: c.endMonth },
  ];
};
